import { supabase } from "../../../lib/supabase";
import type { Database } from "@flexwise/shared/supabase-types";
import type { CheckInOutStudent, StudentNote } from "../types/checkinout";
import { applyDailyLogToCheckInOutStudent } from "@flexwise/shared/utils/attendanceHelpers";
import { mapTodayDataToStudent } from "./attendanceTodayMapper";

function formatSupabaseError(err: any): string {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  const parts = [err.message, err.code, err.details, err.hint]
    .filter(Boolean)
    .map((s: any) => (typeof s === "string" ? s : JSON.stringify(s)));
  return parts.join(" | ") || JSON.stringify(err);
}

async function ensureAuthenticated(): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session && session.access_token) return;
  try {
    await supabase.auth.refreshSession();
  } catch {}
  const {
    data: { session: s2 },
  } = await supabase.auth.getSession();
  if (!s2 || !s2.access_token) throw new Error("AUTH_REQUIRED");
}

async function rpcWithRetry<T = any>(
  fn: () => PromiseLike<{ data: T | null; error: any }>,
): Promise<T | null> {
  await ensureAuthenticated();
  let { data, error } = await fn();
  const msg = error ? formatSupabaseError(error) : "";
  const needsRetry =
    !!error &&
    (/AUTH_REQUIRED/i.test(msg) ||
      /JWT|token|unauth/i.test(msg) ||
      String(error?.code || "") === "P0001");
  if (needsRetry) {
    try {
      await supabase.auth.refreshSession();
    } catch {}
    ({ data, error } = await fn());
  }
  if (error) throw new Error(formatSupabaseError(error));
  return data ?? null;
}

// Type definitions for existing database schema
type StudentDailyLog = {
  id: string;
  student_id: string;
  school_id: string;
  date: string;
  status: string | null;
  note: string | null;
  notes: string | null;
  check_in_time: string | null;
  check_out_time: string | null;
  expected_checkout_time: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
};
type StudentAttendanceLog =
  Database["public"]["Tables"]["student_attendance_logs"]["Row"];

// Clean "today" attendance view types (vw_attendance_checkinout_today_clean)
export interface AttendanceTodayGuardianContact {
  id: string;
  type: string;
  label: string | null;
  value: string;
  is_primary: boolean;
  status: string | null;
  visible?: boolean;
}

export interface AttendanceTodayGuardian {
  profile_id: string;
  first_name: string;
  last_name: string;
  relationship: string | null;
  authorized_for_pickup: boolean;
  pickup_priority: number | null;
  is_primary_guardian: boolean;
  is_primary_contact: boolean;
  contacts: AttendanceTodayGuardianContact[];
}

export interface AttendanceTodayContact {
  id: string;
  type: string;
  label: string | null;
  value: string;
  status: string | null;
  is_primary: boolean | null;
  visible: boolean;
}

export interface AttendanceTodayPickup {
  type: string;
  notes: string | null;
  is_fallback: boolean;
  has_weekly_rule: boolean;
  has_daily_override: boolean;
}

export interface AttendanceTodayData {
  student_id: string;
  school_id: string;
  first_name: string;
  last_name: string;
  class_id: string | null;
  class_name: string | null;

  presence_status: string;
  status_ui?: string | null;
  check_in_time: string | null;
  check_in_by: string | null;
  check_in_by_name?: string | null;
  check_in_method: string | null;
  check_out_time: string | null;
  check_out_by: string | null;
  check_out_by_name?: string | null;
  check_out_method: string | null;
  notes: string | null;
  important_info?: string | null;
  is_late: boolean | null;

  pickup: AttendanceTodayPickup;

  expected_dismissal_time: string | null;
  has_flex_today: boolean | null;

  flex_course: string | null;
  flex_room_id: string | null;
  flex_room_name: string | null;
  flex_room_number: string | null;

  // DEPRECATED: UI no longer uses view-provided student_color; kept for backward compatibility
  student_color: string | null;

  can_view_phones: boolean;
  contact_access_expires_at: string | null;
  has_phone_numbers?: boolean;

  guardians: AttendanceTodayGuardian[];
}

export interface AttendanceTodayRow {
  student_id: string;
  school_id: string;
  data: AttendanceTodayData;
}

export interface DailyLogEntry {
  studentId: string;
  date: string;
  presenceStatus?: string | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  expectedArrivalTime?: string | null;
  expectedCheckoutTime?: string | null;
  notes?: string | null;
  checkOutBy?: string | null;
  checkOutMethod?: string | null;
}

export type DailyLogByStudent = Map<string, DailyLogEntry>;

const DAILY_LOG_BATCH_SIZE = 200;

/**
 * Load today's attendance using the minimal RPC (48% smaller, 43% faster).
 * Only loads columns needed for the list view - guardian/contact details
 * are loaded on demand when the student popup is opened.
 */
export async function loadAttendanceTodayClean(): Promise<
  AttendanceTodayRow[]
> {
  await ensureAuthenticated();

  // Get school_id first for the parameterized RPC
  const schoolId = await getCurrentSchoolId();
  if (!schoolId) {
    throw new Error("Could not determine school ID");
  }

  // Pass browser date so time-travel extensions work for testing historical dates
  const browserDate = new Date();
  const targetDate = `${browserDate.getFullYear()}-${String(
    browserDate.getMonth() + 1,
  ).padStart(2, "0")}-${String(browserDate.getDate()).padStart(2, "0")}`;

  const { data, error } = await supabase.rpc(
    "fn_attendance_list_minimal" as any,
    { p_target_date: targetDate } as any,
  );

  if (error) {
    throw new Error(formatSupabaseError(error));
  }

  const rows = (data || []) as any[];

  return rows.map((row) => {
    // Minimal pickup info - full details loaded on demand
    const pickup: AttendanceTodayPickup = {
      type: (row.pickup_type as string | null) ?? "Abholung",
      notes: null, // Loaded on demand
      is_fallback: false,
      has_weekly_rule: false,
      has_daily_override: false,
    };

    const mapped: AttendanceTodayData = {
      student_id: String(row.student_id),
      school_id: schoolId, // We know this from the request
      first_name: String(row.first_name ?? ""),
      last_name: String(row.last_name ?? ""),
      class_id: (row.class_id as string | null) ?? null,
      class_name: (row.class_name as string | null) ?? null,
      presence_status: String(row.presence_status ?? ""),
      status_ui: (row.status_ui as string | null) ?? null,
      check_in_time: (row.check_in_time as string | null) ?? null,
      check_in_by: null, // Loaded on demand
      check_in_by_name: null, // Loaded on demand
      check_in_method: null, // Loaded on demand
      check_out_time: (row.check_out_time as string | null) ?? null,
      check_out_by: null, // Loaded on demand
      check_out_by_name: null, // Loaded on demand
      check_out_method: null, // Loaded on demand
      notes:
        (row.combined_notes as string | null) ??
        (row.notes as string | null) ??
        null,
      is_late: false,
      pickup,
      expected_dismissal_time:
        (row.expected_dismissal_time as string | null) ?? null,
      has_flex_today:
        (row.has_flex_today as boolean | null) ??
        (row.has_flex as boolean | null) ??
        null,
      flex_course: (row.flex_course as string | null) ?? null,
      // Details loaded on demand via fn_attendance_get_student_detail
      flex_room_id: null,
      flex_room_name: null,
      flex_room_number: null,
      student_color: null,
      can_view_phones: false,
      contact_access_expires_at: null,
      has_phone_numbers: false,
      guardians: [],
    };

    return {
      student_id: mapped.student_id,
      school_id: mapped.school_id,
      data: mapped,
    };
  });
}

export async function loadAttendanceStudentDetail(
  schoolId: string,
  studentId: string,
  date?: string | null,
): Promise<AttendanceTodayRow | null> {
  await ensureAuthenticated();

  const data = await rpcWithRetry<any>(() =>
    supabase.rpc(
      "fn_attendance_get_student_detail" as any,
      {
        p_school_id: schoolId,
        p_student_id: studentId,
        p_date: date ? date.slice(0, 10) : undefined,
      } as any,
    ),
  );

  if (!data) {
    return null;
  }

  return {
    student_id: String((data as any).student_id ?? studentId),
    school_id: String((data as any).school_id ?? schoolId),
    data: data as AttendanceTodayData,
  };
}

function formatDateISO(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  if (!arr.length || size <= 0) return [];
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, (i + 1) * size),
  );
}

function extractDailyLogMetadata(rows: StudentDailyLog[]) {
  const mapNotes: Record<string, { text: string; ts?: string }[]> = {};
  const dailyOverrideMap: Record<string, string> = {};
  const toHHMM = (t?: string | null) => {
    if (!t) return null;
    const m = String(t).match(/^(\d{1,2}):(\d{2})/);
    return m ? `${m[1].padStart(2, "0")}:${m[2]}` : null;
  };

  rows.forEach((r) => {
    const sid = String(r.student_id);
    const raw = (r?.notes || "").toString();
    if (raw.trim()) {
      const ts = (r?.updated_at ||
        r?.check_out_time ||
        r?.check_in_time ||
        null) as string | null;
      // Filter out internal metadata tags used by backend RPCs
      const isMetadataTag = (s: string) =>
        /^class_absence:/i.test(s) || /^method:/i.test(s);
      const extractReadable = (s: string) => {
        const m = s.match(/^(?:diary|reason):(.+)$/i);
        return m ? m[1].trim() : s;
      };
      const lines = raw
        .split(/[\r\n;]+/)
        .map((s: string) => s.trim())
        .filter(Boolean)
        .filter((s: string) => !isMetadataTag(s))
        .map(extractReadable)
        .filter(Boolean);
      if (lines.length) {
        mapNotes[sid] = lines.map((text: string) => ({
          text,
          ts: ts || undefined,
        }));
      }
    }
    const ec = toHHMM(r?.expected_checkout_time as string | null);
    if (ec) {
      const prev = dailyOverrideMap[sid];
      if (!prev || ec < prev) {
        dailyOverrideMap[sid] = ec;
      }
    }
  });

  return { mapNotes, dailyOverrideMap };
}

function enrichStudentsWithDailyLog(
  students: CheckInOutStudent[],
  mapNotes: Record<string, { text: string; ts?: string }[]>,
  overrideMap: Record<string, string>,
): CheckInOutStudent[] {
  return students.map((student) => {
    const extras = mapNotes[student.id] || [];
    const override = overrideMap[student.id];
    let expectedDismissal = student.expectedDismissal as any;
    if (override && (!expectedDismissal || override < expectedDismissal)) {
      expectedDismissal = override;
    }
    const base =
      expectedDismissal && expectedDismissal !== student.expectedDismissal
        ? { ...student, expectedDismissal }
        : student;
    if (!extras.length) return base;
    const existingTexts = new Set((base.notes || []).map((n: any) => n.text));
    const appended = extras
      .filter((e) => !existingTexts.has(e.text))
      .map((e) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        text: e.text,
        timestamp: e.ts
          ? new Date(String(e.ts).replace(" ", "T")).toLocaleString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
        author: "System",
      }));
    return appended.length
      ? { ...base, notes: [...base.notes, ...appended] }
      : base;
  });
}

function applyDailyLogStatus(
  students: CheckInOutStudent[],
  dailyLogByStudent: DailyLogByStudent,
): CheckInOutStudent[] {
  if (!dailyLogByStudent || dailyLogByStudent.size === 0) return students;
  return students.map((student) => {
    const log = dailyLogByStudent.get(student.id);
    if (!log) return student;
    const next = { ...student };
    const likeRow = {
      check_in_time: log.checkInTime ?? null,
      check_out_time: log.checkOutTime ?? null,
      expected_checkout_time: log.expectedCheckoutTime ?? null,
      absent_during_presence: false,
    };
    applyDailyLogToCheckInOutStudent(next as any, likeRow as any);
    return next;
  });
}

/**
 * Data adapter for Check-In/Out functionality
 * Connects to existing student_daily_log and related tables
 * NO BACKEND CHANGES - Uses existing schema
 */

/**
 * Fetch students for check-in/out display
 * Uses existing students table and daily log events
 */
export async function fetchStudentsForCheckInOut(
  schoolId: string,
  date: Date = new Date(),
  classIds: string[] = [],
  search: string = "",
): Promise<CheckInOutStudent[]> {
  try {
    const tryViews = ["vw_react_students_basic"] as const;

    const addFilters = (q: any) => {
      let query = q.eq("school_id", schoolId);
      if (classIds && classIds.length)
        query = query.in("class_id", classIds as any);
      if (search && search.trim()) {
        const term = search.trim();
        query = query.or(
          `first_name.ilike.*${term}*,last_name.ilike.*${term}*`,
        );
      }
      return query;
    };

    const isViewFallbackErr = (err: any) => {
      const code = String(err?.code || "").toUpperCase();
      const status = Number(err?.status || 0);
      const msg = String(err?.message || "").toLowerCase();
      return (
        code.includes("42P01") ||
        code.includes("42S02") ||
        code.includes("42703") ||
        code.includes("57014") ||
        status === 400 ||
        msg.includes("column")
      );
    };

    let data: any[] | null = null;
    let lastErr: any = null;

    for (const view of tryViews) {
      const { data: d, error } = (await addFilters(
        supabase.from(view as any).select("*"),
      )) as any;
      if (!error) {
        data = d as any[];
        break;
      }
      lastErr = error;
      if (!isViewFallbackErr(error)) break;
    }

    if (!data && lastErr) throw lastErr;
    const rows = (data as any[]) || [];
    return rows.map(transformToCheckInOutStudent);
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.warn("Fetch students failed. Returning empty list:", msg);
    return [];
  }
}

export interface LoadAttendanceListParams {
  schoolId: string;
  date: Date;
  classIds: string[];
  search: string;
  forceAll?: boolean;
  presentOnly?: boolean;
}

export interface LoadAttendanceListResult {
  students: CheckInOutStudent[];
  dailyLogByStudent: DailyLogByStudent;
}

// ── Absence notes from student_absence_notes ────────────────────────────

const ABSENCE_TYPE_LABELS: Record<string, string> = {
  krank: "Krank",
  beurlaubt: "Beurlaubt",
  unentschuldigt: "Unentschuldigt",
  entschuldigt: "Entschuldigt",
  sonstiges: "Sonstiges",
  kurzbeschulung: "Kurzbeschulung",
};

interface AbsenceNoteRow {
  id: string;
  student_id: string;
  absence_type: string | null;
  reason: string | null;
  status: string | null;
  absence_status: string | null;
  is_excused: boolean | null;
  start_date: string | null;
  end_date: string | null;
}

export async function fetchAbsenceNotesForDate(
  schoolId: string,
  dateStr: string,
): Promise<AbsenceNoteRow[]> {
  try {
    const { data, error } = await supabase
      .from("student_absence_notes")
      .select(
        "id, student_id, absence_type, reason, status, absence_status, is_excused, start_date, end_date",
      )
      .eq("school_id", schoolId)
      .lte("start_date", dateStr)
      .gte("end_date", dateStr);
    if (error) {
      console.warn(
        "[attendance] Failed to fetch absence notes:",
        error.message,
      );
      return [];
    }
    return (data || []) as AbsenceNoteRow[];
  } catch (e) {
    console.warn("[attendance] Exception fetching absence notes:", e);
    return [];
  }
}

export function enrichStudentsWithAbsenceNotes(
  students: CheckInOutStudent[],
  absenceNotes: AbsenceNoteRow[],
): CheckInOutStudent[] {
  if (!absenceNotes.length) return students;

  // Group absence notes by student_id
  const byStudent = new Map<string, AbsenceNoteRow[]>();
  for (const note of absenceNotes) {
    const list = byStudent.get(note.student_id) || [];
    list.push(note);
    byStudent.set(note.student_id, list);
  }

  return students.map((student) => {
    const notes = byStudent.get(student.id);
    if (!notes?.length) return student;

    const absenceEntries = notes.map((n) => {
      const label =
        ABSENCE_TYPE_LABELS[n.absence_type || ""] ||
        n.absence_type ||
        "Fehlzeit";
      const statusSuffix = n.is_excused ? " (entschuldigt)" : "";
      const reasonText = n.reason ? `: ${n.reason}` : "";
      return {
        id: `absence-${n.id}`,
        text: `${label}${statusSuffix}${reasonText}`,
        timestamp: "",
        author: "Fehlzeiten",
      };
    });

    const existingTexts = new Set(
      (student.notes || []).map((n: any) => n.text),
    );
    const newEntries = absenceEntries.filter((e) => !existingTexts.has(e.text));
    if (!newEntries.length) return student;

    return { ...student, notes: [...(student.notes || []), ...newEntries] };
  });
}

export async function loadAttendanceList(
  params: LoadAttendanceListParams,
): Promise<LoadAttendanceListResult> {
  const { schoolId, date, classIds, search, forceAll, presentOnly } = params;

  if (!schoolId) {
    return { students: [], dailyLogByStudent: new Map() };
  }

  const isToday = (() => {
    try {
      const today = new Date();
      return today.toDateString() === date.toDateString();
    } catch {
      return false;
    }
  })();

  if (
    presentOnly &&
    forceAll &&
    (!classIds || classIds.length === 0) &&
    !search &&
    isToday
  ) {
    const pageSize = 200;
    const all: CheckInOutStudent[] = [];
    let offset = 0;
    for (;;) {
      const page = await fetchPresentTodayList(
        offset,
        pageSize,
        null,
        null,
        schoolId,
      );
      if (!page.length) break;
      all.push(...page);
      if (page.length < pageSize) break;
      offset += pageSize;
    }
    // Enrich with absence notes from student_absence_notes
    const todayStr = new Date().toISOString().slice(0, 10);
    const absenceNotes = await fetchAbsenceNotesForDate(schoolId, todayStr);
    const enrichedAll = enrichStudentsWithAbsenceNotes(all, absenceNotes);
    return { students: enrichedAll, dailyLogByStudent: new Map() };
  }

  const effectiveClassIds = forceAll ? [] : classIds;
  const effectiveSearch = forceAll ? "" : search;

  const studentData = await fetchStudentsForCheckInOut(
    schoolId,
    date,
    effectiveClassIds,
    effectiveSearch,
  );
  // Enrich with absence notes from student_absence_notes
  const dateStr = date.toISOString().slice(0, 10);
  const absenceNotes = await fetchAbsenceNotesForDate(schoolId, dateStr);
  const enrichedStudents = enrichStudentsWithAbsenceNotes(
    studentData,
    absenceNotes,
  );
  const dailyLogByStudent: DailyLogByStudent = new Map();
  return { students: enrichedStudents, dailyLogByStudent };
}

/**
 * Check in a student (writes to existing student_daily_log table)
 */
export async function checkInStudent(
  studentId: string,
  note?: string,
): Promise<void> {
  try {
    const callPrimary = () =>
      supabase.rpc("ops_mark_present_today", {
        p_student_id: studentId,
        p_note: note ?? null,
      });
    try {
      await rpcWithRetry(callPrimary);
    } catch (e: any) {
      const code = (e?.message || "").includes("42883")
        ? "42883"
        : e?.code || "";
      if (
        code === "42883" ||
        /function .* does not exist/i.test(String(e?.message || ""))
      ) {
        await rpcWithRetry(() =>
          supabase.rpc("ops_check_in_student_react", {
            p_student_id: studentId,
            p_note: note ?? null,
          }),
        );
      } else {
        throw e;
      }
    }
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.error("Error checking in student:", msg);
    throw new Error(msg);
  }
}

/**
 * Check out a student (writes to existing student_daily_log table)
 */
export async function checkOutStudent(
  studentId: string,
  method: string,
  note?: string,
): Promise<void> {
  try {
    await rpcWithRetry(() =>
      supabase.rpc("ops_check_out_student_react", {
        p_student_id: studentId,
        p_method: method,
        p_note: note ?? null,
      }),
    );
    // NOTE: ops_check_out_student_react already appends the note to student_daily_log.notes.
    // A previous safety-net call to ops_add_note_react here was removed because it caused
    // duplicate notes (e.g. "Entschuldigt: Kurzbeschulung" appearing twice).
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.error("Error checking out student:", msg);
    throw new Error(msg);
  }
}

/**
 * Cancel today's checkout (re-open presence)
 */
export async function cancelCheckoutStudent(studentId: string): Promise<void> {
  try {
    const callCancel = () =>
      supabase.rpc(
        "ops_cancel_checkout" as any,
        { p_student_id: studentId } as any,
      );
    try {
      await rpcWithRetry(callCancel);
    } catch (e: any) {
      const code = (e?.message || "").includes("42883")
        ? "42883"
        : e?.code || "";
      if (
        code === "42883" ||
        /function .* does not exist/i.test(String(e?.message || ""))
      ) {
        await rpcWithRetry(() =>
          supabase.rpc(
            "ops_mark_present_today" as any,
            { p_student_id: studentId, p_note: null } as any,
          ),
        );
      } else {
        throw e;
      }
    }
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.error("Error canceling checkout:", msg);
    throw new Error(msg);
  }
}

/**
 * Batch check-in operation
 */
export async function batchCheckInStudents(
  studentIds: string[],
): Promise<void> {
  try {
    await rpcWithRetry(() =>
      supabase.rpc("ops_batch_check_in_react", { p_student_ids: studentIds }),
    );
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.error("Error batch checking in students:", msg);
    throw new Error(msg);
  }
}

/**
 * Batch check-out operation
 */
export async function batchCheckOutStudents(
  operations: Array<{ studentId: string; method: string; note?: string }>,
): Promise<void> {
  try {
    const payload = operations.map((op) => ({
      student_id: op.studentId,
      method: op.method,
      note: op.note ?? null,
    }));
    await rpcWithRetry(() =>
      supabase.rpc("ops_batch_check_out_react", { p_ops: payload }),
    );
    // Ensure notes persist even if batch RPC ignores note
    const withNotes = operations.filter((op) => op.note && op.note.trim());
    if (withNotes.length) {
      await Promise.all(
        withNotes.map((op) =>
          rpcWithRetry(() =>
            supabase.rpc(
              "ops_add_note_react" as any,
              { p_student_id: op.studentId, p_note: op.note } as any,
            ),
          ),
        ),
      );
    }
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.error("Error batch checking out students:", msg);
    throw new Error(msg);
  }
}

/**
 * Add a note to a student (can be stored in student_daily_log as note event)
 */
export async function addStudentNote(
  studentId: string,
  noteText: string,
): Promise<void> {
  try {
    await rpcWithRetry(() =>
      supabase.rpc("ops_add_note_react", {
        p_student_id: studentId,
        p_note: noteText,
      }),
    );
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.error("Error adding student note:", msg);
    throw new Error(msg);
  }
}

// Unified attendance action adapter. This currently delegates to existing ops_* RPCs and will later call the unified mutation RPC(s)
// described in section 13 "Proposed Unified Attendance RPCs" of docs/refactoring-rtk-implementation/ATTENDANCE_RTK_REFACTOR_MS.md.
export interface ApplyAttendanceActionParams {
  schoolId: string | null;
  date: string; // YYYY-MM-DD
  studentIds: string[];
  action: "check_in" | "check_out" | "batch_check_in" | "batch_check_out";
  method?: string;
  meta?: Record<string, any>;
}

export async function applyAttendanceAction(
  params: ApplyAttendanceActionParams,
): Promise<void> {
  const { studentIds, action, method, meta } = params;
  const note: string | undefined =
    typeof meta?.note === "string" ? meta.note : undefined;

  if (!studentIds.length) return;

  if (action === "check_in") {
    await checkInStudent(studentIds[0], note);
    return;
  }

  if (action === "check_out") {
    await checkOutStudent(studentIds[0], method || "default", note);
    return;
  }

  if (action === "batch_check_in") {
    await batchCheckInStudents(studentIds);
    return;
  }

  if (action === "batch_check_out") {
    const ops = studentIds.map((id) => ({
      studentId: id,
      method: method || "default",
      note,
    }));
    await batchCheckOutStudents(ops);
    return;
  }
}

// Unified list adapter placeholder. This currently composes existing views/RPCs via loadAttendanceList and is intended to call
// the unified list RPC (for example fn_attendance_get_list) described in section 13 "Proposed Unified Attendance RPCs" of
// docs/refactoring-rtk-implementation/ATTENDANCE_RTK_REFACTOR_MS.md.
export interface FetchAttendanceListUnifiedParams {
  schoolId: string | null;
  date: string; // YYYY-MM-DD
  classIds: string[];
  search: string;
  mode?: "checkin" | "checkout";
  showOnlyPresent?: boolean;
}

export async function fetchAttendanceListUnified(
  params: FetchAttendanceListUnifiedParams,
): Promise<CheckInOutStudent[]> {
  const { schoolId, date, classIds, search } = params;
  const day = date.slice(0, 10);
  const result = await loadAttendanceList({
    schoolId: schoolId || "",
    date: new Date(day),
    classIds,
    search,
    forceAll: false,
  });
  return result.students;
}

// Lightweight present-today list via client-side fast path
export async function fetchPresentTodayList(
  offset: number = 0,
  limit: number = 200,
  search?: string | null,
  classId?: string | null,
  schoolId?: string | null,
): Promise<CheckInOutStudent[]> {
  try {
    const sid = schoolId || (await getCurrentSchoolId());
    if (!sid) {
      return [];
    }

    const rows = await loadAttendanceTodayClean();
    const bySchool = rows.filter((r) => String(r.school_id) === String(sid));
    const baseList = bySchool.map((r) => mapTodayDataToStudent(r.data));
    if (!baseList || !Array.isArray(baseList) || baseList.length === 0) {
      return [];
    }

    const term = (search || "").toString().trim().toLowerCase();
    const classFilter = classId ? String(classId) : null;

    const filtered = baseList.filter((s) => {
      if (classFilter) {
        const cid = s.classId ? String(s.classId) : "";
        if (cid !== classFilter) {
          return false;
        }
      }
      if (term) {
        const name = `${s.firstName || ""} ${s.lastName || ""}`.toLowerCase();
        if (!name.includes(term)) {
          return false;
        }
      }
      return true;
    });

    return filtered.slice(offset, offset + limit);
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.error("Error fetching present-today list (clean view path):", msg);
    return [];
    throw new Error(msg);
  }
}

// Strict absent helpers (feature-detected on server)
export async function markAbsentExcused(
  studentId: string,
  reason?: string | null,
): Promise<void> {
  try {
    const { error } = await supabase.rpc("ops_mark_absent_excused", {
      p_student_id: studentId,
      p_reason: reason ?? null,
    } as any);
    if (error) throw error;
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.error("Error marking absent_excused:", msg);
    throw new Error(msg);
  }
}

export async function markAbsentUnexcused(
  studentId: string,
  reason?: string | null,
): Promise<void> {
  try {
    const { error } = await supabase.rpc("ops_mark_absent_unexcused", {
      p_student_id: studentId,
      p_reason: reason ?? null,
    } as any);
    if (error) throw error;
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.error("Error marking absent_unexcused:", msg);
    throw new Error(msg);
  }
}

export async function markAbsentUnclear(
  studentId: string,
  reason?: string | null,
): Promise<void> {
  try {
    const { error } = await supabase.rpc("ops_mark_absent_unclear", {
      p_student_id: studentId,
      p_reason: reason ?? null,
    } as any);
    if (error) throw error;
  } catch (error) {
    const msg = formatSupabaseError(error);
    console.error("Error marking absent_unclear:", msg);
    throw new Error(msg);
  }
}

/**
 * Helper function to get current school ID from user context
 */
async function getCurrentSchoolId(): Promise<string> {
  // Prefer secure server-side resolution via RPC (respects RLS)
  try {
    const { data: rpcData, error: rpcError } = await supabase.rpc(
      "get_user_school_id",
    );
    if (!rpcError && rpcData) return rpcData as string;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[Attendance] getCurrentSchoolId RPC fallback failed", err);
    }
  }

  return "";
}

/**
 * Transform database records to CheckInOutStudent format
 */
export function transformToCheckInOutStudent(row: any): CheckInOutStudent {
  const presence: string | null = row?.presence_status ?? null;
  // Base status from presence flag
  let status: CheckInOutStudent["status"] = (() => {
    switch (presence) {
      case "present":
      case "late":
      case "partial":
        return "anwesend";
      case "absent_excused":
        return "entschuldigt";
      case "absent_unexcused":
      case "absent_unclear":
        return "abwesend";
      case "left_early":
      case "left_without_notice":
        return "entlassen";
      default:
        return "abwesend";
    }
  })();

  const timeToStr = (ts?: string | null) => {
    if (!ts) return undefined;
    try {
      const s = String(ts);
      const m = s.match(/^\s*(\d{1,2}):(\d{2})/);
      if (m) {
        const hh = m[1].padStart(2, "0");
        const mm = m[2];
        return `${hh}:${mm}`;
      }
      const d = new Date(s.replace(" ", "T"));
      if (!isNaN(d.getTime())) {
        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        return `${hh}:${mm}`;
      }
    } catch {}
    return undefined;
  };

  const hasFlex = !!row?.has_flex_module;
  const hasWeeklyRule = !!(row?.pickup_rule && String(row.pickup_rule).trim());

  const normalizePickupRule = (raw?: string | null) => {
    const s = (raw || "").toString().trim();
    if (!s) return "";
    // Try to parse patterns like "Abholung: 2: Text 3: Text 4: Text ..."
    try {
      const afterPrefix = s.replace(/^Abholung:\s*/i, "").trim();
      // Split into tokens by day markers (1-7 or Mo-So), keep pairs
      const parts = afterPrefix
        .split(/\s*(?=(?:[1-7]|Mo|Di|Mi|Do|Fr|Sa|So)\s*:)\s*/i)
        .filter(Boolean);
      if (parts.length <= 1) return s; // nothing to normalize
      const entries: Array<{ day: string; text: string }> = [];
      for (const p of parts) {
        const m = p.match(/^((?:[1-7]|Mo|Di|Mi|Do|Fr|Sa|So))\s*:\s*(.*)$/i);
        if (!m) {
          entries.push({ day: "", text: p.trim() });
          continue;
        }
        entries.push({ day: m[1], text: m[2].trim() });
      }
      const texts = entries.map((e) => e.text).filter(Boolean);
      if (texts.length === 0) return s;
      const allSame = texts.every(
        (t) => t.toLowerCase() === texts[0].toLowerCase(),
      );
      if (allSame) {
        // If all identical per-day notes, collapse to single
        const collapsed = texts[0];
        return collapsed ? `Abholung: ${collapsed}` : "Abholung";
      }
      // Otherwise, keep original string
      return s;
    } catch {
      return s;
    }
  };

  const dismissalFromRow = (() => {
    const raw =
      row?.expected_dismissal ??
      row?.expected_dismissal_time ??
      row?.dismissal_time ??
      row?.last_lesson_end ??
      row?.pickup_time ??
      null;
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
      if (match) {
        const hh = match[1].padStart(2, "0");
        const mm = match[2];
        return `${hh}:${mm}`;
      }
    }
    return undefined;
  })();

  const expected =
    dismissalFromRow ||
    (hasFlex ? "16:00" : hasWeeklyRule ? "14:30" : undefined);

  // If student has a check-in time today and no check-out yet, they are currently present.
  // This overrides an "absent_*" presence flag coming from course attendance (e.g., absent during presence in FLEX).
  const hasCheckIn = !!row?.check_in_time;
  const hasCheckOut = !!row?.check_out_time;
  const absentDuringPresence = !!row?.absent_during_presence;
  if (hasCheckIn && !hasCheckOut) {
    status = "anwesend";
  } else if (hasCheckOut) {
    status = "entlassen";
  } else if (absentDuringPresence && hasCheckIn) {
    status = "anwesend";
  }

  // Derive a primary guardian contact if available
  let guardianName: string | undefined = undefined;
  let guardianRelationship: string | undefined = undefined;
  let guardianPhone: string | undefined = undefined;

  // Parse contacts list from view if available
  let contactsPlain: string | undefined = undefined;
  let contactsList:
    | { name: string; relationship: string; phone: string }[]
    | undefined = undefined;
  try {
    if (typeof row?.contacts_plain === "string" && row.contacts_plain.trim()) {
      contactsPlain = row.contacts_plain.trim();
    }
    if (Array.isArray(row?.contacts_json)) {
      contactsList = row.contacts_json
        .map((c: any) => ({
          name: String(c?.contact_name ?? ""),
          relationship: String(
            c?.relationship_display ?? c?.relationship_to_child ?? "",
          ),
          phone: String(c?.phone ?? ""),
          adultId: (c?.adult_profile_id ?? undefined) as string | undefined,
          authorized: c?.authorized_for_pickup === true,
          isPrimary:
            c?.is_primary_guardian === true || Number(c?.pickup_priority) === 1,
          pickupPriority: (c?.pickup_priority ?? null) as number | null,
          className: c?.student_class_name
            ? String(c.student_class_name)
            : undefined,
        }))
        .filter(
          (c: { name: string; relationship: string; phone: string }) =>
            c.name || c.relationship || c.phone,
        );
    }
  } catch {}

  try {
    // 1) From flattened columns if view provides them
    const gFirst =
      (
        row?.guardian_first_name ??
        row?.primary_guardian_first_name ??
        ""
      ).trim?.() || "";
    const gLast =
      (
        row?.guardian_last_name ??
        row?.primary_guardian_last_name ??
        ""
      ).trim?.() || "";
    const gRel =
      (
        row?.guardian_relationship ??
        row?.primary_guardian_relationship ??
        row?.relation_description ??
        ""
      ).trim?.() || "";
    const gPhoneFlat =
      (row?.guardian_phone ?? row?.primary_guardian_phone ?? "").trim?.() || "";

    if (gFirst || gLast || gRel || gPhoneFlat) {
      guardianName =
        [gFirst, gLast].filter(Boolean).join(" ").trim() || undefined;
      guardianRelationship = gRel || undefined;
      guardianPhone = gPhoneFlat || undefined;
    }

    // 2) From aggregated JSON array parent_contacts if present
    if (!guardianPhone || !guardianName) {
      const contacts = Array.isArray(row?.parent_contacts)
        ? row.parent_contacts
        : null;
      if (contacts && contacts.length) {
        // Prefer an entry with a phone, otherwise first entry
        const pick =
          contacts.find((c: any) => (c?.phone ?? "").toString().trim()) ||
          contacts[0];
        const fn = (pick?.first_name ?? "").toString().trim();
        const ln = (pick?.last_name ?? "").toString().trim();
        const rel = (pick?.relationship ?? "").toString().trim();
        const ph = (pick?.phone ?? "").toString().trim();
        guardianName =
          guardianName ||
          [fn, ln].filter(Boolean).join(" ").trim() ||
          undefined;
        guardianRelationship = guardianRelationship || rel || undefined;
        guardianPhone = guardianPhone || ph || undefined;
      }
    }
  } catch {}

  // Build notes from daily log string if present
  const notesArr: any[] = [];
  try {
    const noteText: string | null = row?.notes ?? null;
    if (noteText && typeof noteText === "string") {
      // Split by newlines AND semicolons, then filter out internal metadata tags
      // that are used by backend RPCs (e.g. class_absence:UUID, method:class_trip)
      const isMetadataTag = (s: string) =>
        /^class_absence:/i.test(s) || /^method:/i.test(s);
      const extractReadable = (s: string) => {
        // diary: and reason: prefixes contain human-readable content — extract the value
        const m = s.match(/^(?:diary|reason):(.+)$/i);
        return m ? m[1].trim() : s;
      };
      // note:<Author>:<DD.MM.YYYY HH:MM>:<body> — Fehlzeit attendance-thread
      // notes (student_attendance_notes), emitted by fn_attendance_list_minimal.
      const parseNoteTag = (
        s: string,
      ): { author: string; timestamp: string; text: string } | null => {
        const m = s.match(
          /^note:([^:]+):(\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}):(.+)$/i,
        );
        if (m)
          return {
            author: m[1].trim(),
            timestamp: m[2].trim(),
            text: m[3].trim(),
          };
        return null;
      };
      const rawLines = noteText
        .split(/[\r\n;]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .filter((s) => !isMetadataTag(s));
      const fallbackTs = (() => {
        try {
          const d = row?.updated_at || row?.check_in_time || null;
          return d
            ? new Date(String(d).replace(" ", "T")).toLocaleString("de-DE", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : undefined;
        } catch {
          return undefined;
        }
      })();
      for (const line of rawLines) {
        const nid =
          typeof crypto !== "undefined" && (crypto as any).randomUUID
            ? (crypto as any).randomUUID()
            : `${Math.random().toString(36).slice(2)}-${Date.now()}`;
        const noted = parseNoteTag(line);
        if (noted) {
          notesArr.push({
            id: nid,
            text: noted.text,
            timestamp: noted.timestamp,
            author: noted.author,
          });
          continue;
        }
        const readable = extractReadable(line);
        if (!readable) continue;
        notesArr.push({
          id: nid,
          text: readable,
          timestamp: fallbackTs,
          author: "System",
        });
      }
    }
  } catch {}
  // System note if absent in a course while still checked-in today (unexcused only)
  try {
    const isCurrentlyInSchool = !!row?.check_in_time && !row?.check_out_time;
    const isUnexcused =
      (typeof presence === "string" &&
        (presence === "absent_unexcused" || presence === "absent_unclear")) ||
      (!!row?.absent_during_presence && row?.absence_is_excused === false);
    if (isCurrentlyInSchool && isUnexcused) {
      const courseName = (
        row?.course ||
        row?.absent_course ||
        row?.course_name ||
        "Modul"
      ).toString();
      const msg = `Unentschuldigte Abwesenheit im Modul '${courseName}' – Klärung erforderlich`;
      if (
        !notesArr.some(
          (n) =>
            typeof n?.text === "string" &&
            n.text.includes("Klärung erforderlich"),
        )
      ) {
        const tsRaw =
          row?.absent_ts ||
          row?.absent_lesson_start ||
          row?.lesson_start ||
          row?.updated_at ||
          null;
        const ts = (() => {
          try {
            return tsRaw
              ? new Date(String(tsRaw).replace(" ", "T")).toLocaleString(
                  "de-DE",
                  {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )
              : undefined;
          } catch {
            return undefined;
          }
        })();
        const nid =
          typeof crypto !== "undefined" && (crypto as any).randomUUID
            ? (crypto as any).randomUUID()
            : `${Math.random().toString(36).slice(2)}-${Date.now()}`;
        notesArr.push({ id: nid, text: msg, timestamp: ts, author: "System" });
      }
      // Reflect as special status in UI
      status = "klärungsbedarf";
    }
  } catch {}

  return {
    id: row.id,
    firstName: row.first_name ?? "",
    lastName: row.last_name ?? "",
    class: row.class_name ?? row.class_id ?? "N/A",
    classId: row.class_id ?? undefined,
    course: row.course ?? undefined,
    status,
    checkInTime: timeToStr(row.check_in_time) ?? undefined,
    checkInStaff: undefined,
    checkInById: row.check_in_by ?? undefined,
    checkOutTime: timeToStr(row.check_out_time) ?? undefined,
    checkOutStaff: undefined,
    checkOutById: row.check_out_by ?? undefined,
    checkOutMethod: row.check_out_method ?? undefined,
    pickupRule: normalizePickupRule(row.pickup_rule ?? ""),
    pickupIsFallback: !!(Object.prototype.hasOwnProperty.call(
      row,
      "pickup_rule_today",
    ) ||
    Object.prototype.hasOwnProperty.call(row, "pickup_type_today") ||
    Object.prototype.hasOwnProperty.call(row, "special_rule_today")
      ? !(
          row?.special_rule_today ||
          row?.pickup_type_today ||
          row?.pickup_rule_today
        )
      : false),
    specialRuleToday: (() => {
      const v = row?.special_rule_today ?? undefined;
      return v ? normalizePickupRule(v as any) : undefined;
    })(),
    expectedDismissal: expected,
    hasFlexModule: !!row.has_flex_module,
    notes: notesArr,
    hasRestriction: !!row.has_restriction,
    hasMedicalNote: !!row.has_medical_note,
    parentContact: row.parent_contact ?? "",
    emergencyContact: row.emergency_contact ?? undefined,
    guardianName,
    guardianRelationship,
    guardianPhone,
    contactsPlain,
    contacts: contactsList,
    // Extras for Klassenliste enrichment (if columns exist in the view)
    birthdateISO:
      (row?.birthdate ?? row?.date_of_birth ?? undefined) || undefined,
    allergiesText:
      (row?.allergies ?? row?.medical_note ?? undefined) || undefined,
    importantInfoText: (row?.important_info ?? undefined) || undefined,
    sensitiveDataText: (row?.sensitive_data ?? undefined) || undefined,
    butType: (row?.but_type ?? undefined) || undefined,
    butValidUntil: (row?.but_valid_until ?? undefined) || undefined,
  } as CheckInOutStudent & {
    birthdateISO?: string;
    allergiesText?: string;
    importantInfoText?: string;
    sensitiveDataText?: string;
    butType?: string;
    butValidUntil?: string;
  };
}
